/**
 * ProBar.ts — the "Pro-Bar": one pro-grade, lit-CRT value-bar (BL-56, ART §9 + §6.8).
 *
 * The HUD had no fill-bars: integrity/wave were counters, payload was chips,
 * skill was slots. BL-56 ("all the bars") INTRODUCES lit value-bars, and this is
 * the single component every one of them reuses — held to the SAME light language
 * as the arena and the S88 skill-cards (ART §9 "panels are *lit* … a hot edge and
 * a falloff … consistent with the §6.1 emissive-core thinking"):
 *
 *   • the TRACK is a recessed lit-CRT channel (rim-dark inner gradient over the
 *     central `PANEL.BG`), not a 1-px box;
 *   • the FILL is the value colour as an emissive volume — a `mid` body under a
 *     near-white `core` highlight; the FILL's leading edge is the brightest
 *     column, so when the host canvas is run through the SHARED `UIBloom`
 *     (the arena GLSL, reused) the bar's hot edge flares exactly like an entity
 *     core (it clears `UI_BLOOM.threshold`);
 *   • STATE drives the glow — `active`/`warn`/`danger` glow hot, `idle`/`disabled`
 *     dim — material communicating state (ART §9);
 *   • the changing value FLASHES on change (§6.8 `decay`), reduced-motion-safe.
 *
 * Centralised on the S86/S87 foundation: every colour/material stop derives from
 * the ONE shared transform (`deriveRamp` — core = base→white 60 %, rim =
 * base→black 72 %) seeded by a CENTRAL palette colour (`COLORS`/`RARITY`), and
 * the glow routes through the shared `UIBloom` — NOT a new hardcoded colour set
 * and NOT a per-bar WebGL context. Text is crisp DOM, layered ABOVE the canvas,
 * never a bloom source (readability is sacred).
 *
 * Two halves, mirroring `SkillCard`:
 *   - {@link drawProBar} — the pure canvas primitive (track/fill/edge/segments/
 *     flash); takes a 2D context so it is testable headless with a mock ctx;
 *   - {@link ProBar} — the DOM-mountable component (a `<canvas>` behind a crisp
 *     DOM label) that owns the dirty-redraw + the shared bloom + the §6.8 flash.
 *
 * Render-only (Layer 4) → determinism-neutral; the sim never sees it.
 */

import { deriveRamp, type Ramp } from "../rendering/EmissiveMaterial.ts";
import { displayDpr } from "../utils/displayDpr.ts";
import { decay } from "../rendering/AnimationUtils.ts";
import { PANEL } from "../rendering/designTokens.ts";
import { COLORS } from "../core/constants.ts";
import { motionScale, isReducedMotion } from "../engine/MotionSettings.ts";
import { UIBloom } from "./UIBloom.ts";
import {
  UI_FONT_STACK,
  UI_TYPE,
  UI_BLOOM,
  UI_DURATION,
  roleFontSize,
  withUiScale,
} from "./uiTokens.ts";

/** Material/glow state — drives brightness, per ART §9. */
export type ProBarState = "active" | "idle" | "disabled" | "warn" | "danger";

/** Bar shape — a full `bar` (track + fill) or a thin `underline` (skill slots). */
export type ProBarVariant = "bar" | "underline";

/** Flash duration after a value change (ms) — the §6.8 value-felt window. */
export const FLASH_MS = UI_DURATION.flash;
/** Peak flash overlay alpha (before reduced-motion damping). */
export const FLASH_PEAK = 0.55;
/** Glow alpha per state — `active`/`danger` hot, `disabled` near-dark. */
export const STATE_GLOW: Record<ProBarState, number> = {
  active: 1.0,
  warn: 0.95,
  danger: 1.0,
  idle: 0.55,
  disabled: 0.28,
};

/** Clamp the fill ratio to 0..1. Pure. */
export function fillRatio(value: number, max: number): number {
  if (!(max > 0)) return 0;
  const r = value / max;
  return r < 0 ? 0 : r > 1 ? 1 : r;
}

/**
 * The integrity state for a fill ratio (the BL-56 headline thresholds):
 * `danger` < 20 %, `warn` < 50 %, else `active`. Pure — the ONE place the
 * "mainframe is dying" escalation is decided, reused by the HUD + the demo.
 */
export function integrityState(ratio: number): ProBarState {
  if (ratio < 0.2) return "danger";
  if (ratio < 0.5) return "warn";
  return "active";
}

/**
 * The integrity bar's identity colour for a state — the player-identity green
 * shifting to amber → red as it depletes. All three come from the CENTRAL
 * palette (`COLORS`), never a new inline hex (I4).
 */
export function integrityColor(state: ProBarState): string {
  if (state === "danger") return COLORS.ORACLE_RED;
  if (state === "warn") return COLORS.AMBER_WARN;
  return COLORS.SENTINEL_GREEN;
}

/**
 * Value-flash overlay alpha, given ms since the last change. Mirrors the §6.8
 * `decay` shape (fast bright departure, long fade tail) as `1 - decay(t)`,
 * scaled to {@link FLASH_PEAK}. Returns 0 once elapsed ≥ {@link FLASH_MS} or
 * when motion is reduced (`scale` 0) — the bar still updates, it just doesn't
 * pulse. Pure (takes the motion scale so it's testable without globals).
 */
export function flashAlpha(
  elapsedMs: number,
  scale: number = motionScale(),
): number {
  if (scale <= 0) return 0;
  if (elapsedMs < 0 || elapsedMs >= FLASH_MS) return 0;
  const t = elapsedMs / FLASH_MS;
  return (1 - decay(t)) * FLASH_PEAK * scale;
}

/** The geometry + material inputs for a single {@link drawProBar} pass. */
export interface ProBarDrawSpec {
  /** Derived material ramp (from `deriveRamp(color)`). */
  ramp: Ramp;
  /** Fill ratio 0..1 (already clamped). */
  ratio: number;
  /** State glow multiplier (see {@link STATE_GLOW}). */
  glow: number;
  /** Discrete segment count — draws tick dividers (0/1 = smooth). */
  segments?: number;
  /** Value-flash overlay alpha (0 = settled). */
  flash?: number;
  /** `underline` skips the track body for a thin depleting rule. */
  variant?: ProBarVariant;
  /**
   * BL-63 — per-segment emissive ramps (one per discrete segment). When present
   * (and length > 1) the fill is drawn as separate lit cells, each in its OWN
   * colour by position — the wave bar's green→brass→red heat ramp (the fill
   * reads as progress AND heat). Replaces the single-gradient fill + the tick
   * dividers (the inter-cell gaps ARE the dividers). The single-colour path is
   * untouched when this is omitted.
   */
  segmentRamps?: Ramp[] | undefined;
}

/**
 * Draw a Pro-Bar into `ctx` at (x, y, w, h). The caller owns the canvas; running
 * that canvas through `UIBloom.process` afterwards is what gives the leading
 * edge its glow (the draw alone is the crisp body). Pure w.r.t. globals — every
 * colour comes from the passed `ramp` (deriveRamp) + the central `PANEL.BG`.
 */
export function drawProBar(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  spec: ProBarDrawSpec,
): void {
  const { ramp, glow } = spec;
  const ratio = spec.ratio < 0 ? 0 : spec.ratio > 1 ? 1 : spec.ratio;
  const variant = spec.variant ?? "bar";
  const fillW = Math.round(w * ratio);

  ctx.save();

  if (variant === "bar") {
    // ── Track: recessed lit-CRT channel (rim-dark inner gradient) ──────────
    const trackGrad = ctx.createLinearGradient(x, y, x, y + h);
    trackGrad.addColorStop(0, PANEL.BG);
    trackGrad.addColorStop(0.5, ramp.rim);
    trackGrad.addColorStop(1, PANEL.BG);
    ctx.fillStyle = trackGrad;
    ctx.fillRect(x, y, w, h);

    // Emissive edge (a hot rim, not a 1-px line) — brighter when active.
    ctx.globalAlpha = 0.4 + 0.5 * glow;
    ctx.strokeStyle = ramp.mid;
    ctx.lineWidth = 1;
    ctx.strokeRect(x + 0.5, y + 0.5, w - 1, h - 1);
    ctx.globalAlpha = 1;
  }

  const heatSegs = spec.segmentRamps;
  if (heatSegs && heatSegs.length > 1) {
    // ── Per-segment heat fill (BL-63 wave bar) — each lit cell its OWN colour ─
    // by position, so the fill reads as progress AND heat. The inter-cell gaps
    // double as the dividers, so the single-gradient fill + tick step are
    // skipped on this path.
    drawHeatSegments(ctx, x, y, w, h, ratio, heatSegs, glow);
  } else {
    // ── Fill: emissive volume (mid body under a near-white core line) ────────
    if (fillW > 0) {
      const fillGrad = ctx.createLinearGradient(x, y, x, y + h);
      fillGrad.addColorStop(0, ramp.mid);
      fillGrad.addColorStop(0.45, ramp.core); // hot core → clears bloom
      fillGrad.addColorStop(0.55, ramp.core);
      fillGrad.addColorStop(1, ramp.mid);
      ctx.globalAlpha = glow;
      ctx.fillStyle = fillGrad;
      ctx.fillRect(x, y, fillW, h);

      // Leading-edge highlight — the brightest column, the bloom anchor (the
      // material core, already near-white, so it clears UI_BLOOM.threshold).
      ctx.globalAlpha = glow;
      ctx.fillStyle = ramp.core;
      ctx.fillRect(x + Math.max(0, fillW - 2), y, 2, h);
      ctx.globalAlpha = 1;
    }

    // ── Segments: tick dividers (e.g. the 10-segment integrity meter) ──────
    const segs = spec.segments ?? 0;
    if (segs > 1) {
      ctx.globalAlpha = 0.6;
      ctx.fillStyle = PANEL.BG;
      for (let i = 1; i < segs; i++) {
        ctx.fillRect(x + Math.round((w * i) / segs) - 1, y, 1, h);
      }
      ctx.globalAlpha = 1;
    }
  }

  // ── Value-flash overlay (§6.8 decay) — the material core, never raw white ─
  const fa = spec.flash ?? 0;
  if (fa > 0) {
    ctx.globalAlpha = fa;
    ctx.fillStyle = ramp.core;
    ctx.fillRect(x, y, Math.max(fillW, 2), h);
    ctx.globalAlpha = 1;
  }

  ctx.restore();
}

/**
 * BL-63 — draw the fill as discrete lit cells, each in its own emissive ramp by
 * position (the wave bar's green→brass→red heat). Partial cells (the leading
 * edge) are filled proportionally so the bar still moves smoothly between whole
 * waves. A 1-px gap between cells reads as the segment divider. Each cell carries
 * a near-white `core` leading column so the shared bloom flares it exactly like
 * the single-colour bar.
 */
function drawHeatSegments(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  ratio: number,
  ramps: Ramp[],
  glow: number,
): void {
  const n = ramps.length;
  const lit = ratio * n; // fractional number of lit cells
  const cell = w / n;
  const gap = cell > 3 ? 1 : 0; // a thin divider between cells when there's room
  for (let i = 0; i < n; i++) {
    const amt = Math.max(0, Math.min(1, lit - i)); // 0..1 of this cell filled
    if (amt <= 0) continue;
    const r = ramps[i]!;
    const sx = x + i * cell;
    const sw = Math.max(0, cell - gap);
    const fw = sw * amt;
    if (fw <= 0) continue;
    const g = ctx.createLinearGradient(sx, y, sx, y + h);
    g.addColorStop(0, r.mid);
    g.addColorStop(0.45, r.core);
    g.addColorStop(0.55, r.core);
    g.addColorStop(1, r.mid);
    ctx.globalAlpha = glow;
    ctx.fillStyle = g;
    ctx.fillRect(sx, y, fw, h);
    // Leading-edge highlight (the bloom anchor) on this cell.
    ctx.fillStyle = r.core;
    ctx.fillRect(sx + Math.max(0, fw - 1.5), y, 1.5, h);
  }
  ctx.globalAlpha = 1;
}

// ─── Shared WebGL bloom (one context for all bars; the arena GLSL, reused) ────
//
// Mirrors SkillEmblem.sharedBloom: a SINGLE shared UIBloom instance feeds every
// Pro-Bar's glow, so the HUD never spins a WebGL context per bar (the S88
// one-shared-instance contract). Feature-detected → null in headless / no-WebGL2
// so tests and the BL-44 degrade rung never touch a GL context.

let sharedBloom: UIBloom | null | undefined;

/**
 * The ONE shared UI-bloom instance (feature-detected; null in headless / no
 * WebGL2). Every Pro-Bar AND the persistent `WaveFire` route their hot edges
 * through this single instance — the HUD never spins a WebGL context per glowing
 * element (the S88 one-shared-instance contract). Exported so siblings on the
 * top bar (BL-58) reuse it rather than building a second pipeline.
 */
export function getSharedUIBloom(): UIBloom | null {
  if (sharedBloom !== undefined) return sharedBloom;
  sharedBloom = null;
  try {
    if (typeof document === "undefined") return null;
    const probe = document.createElement("canvas").getContext("webgl2");
    if (!probe) return null;
    sharedBloom = new UIBloom();
  } catch {
    sharedBloom = null;
  }
  return sharedBloom;
}

/** How far the lit canvas overscans the bar box (px) so the edge glow / bloom
 *  flare has room to breathe past the silhouette instead of clipping. */
const OVERSCAN = 6;

export interface ProBarOptions {
  /** Base colour (a central palette hex) — the ramp is derived from it. */
  color: string;
  /** Full-scale value. */
  max: number;
  /** Current value (default 0). */
  value?: number;
  /** Discrete segment count — draws tick dividers (e.g. 10 integrity segments). */
  segments?: number;
  /** Material/glow state (default "active"). */
  state?: ProBarState;
  /** Bar shape — full `bar` or thin `underline` (default "bar"). */
  variant?: ProBarVariant;
  /** Optional left-aligned label (crisp DOM, §9 `caption` role). */
  label?: string;
  /** Optional right-aligned value text (crisp DOM, §9 `numeric`); "" suppresses;
   *  omitted → `value/max`. */
  valueText?: string | (() => string);
  /** Extra root classes. */
  extraClasses?: string[];
  /**
   * BL-63 — per-segment base colours (one central-palette hex per discrete
   * segment). When set (length > 1) the fill is drawn as lit cells, each in its
   * own colour by position (the wave heat ramp green→brass→red). Implies a
   * segmented bar; the segment count is this array's length.
   */
  segmentColors?: string[];
}

/**
 * ProBar — the DOM-mountable lit value-bar. A `<canvas>` carries the engine
 * material (dirty-redrawn on value/state change, NOT per-frame); the crisp DOM
 * label/value sits above it (never a bloom source). The §6.8 value-flash
 * self-drives a finite RAF that settles by {@link FLASH_MS}; everything is
 * reduced-motion-gated.
 */
export class ProBar {
  /** The bar root — crisp DOM text on top of the lit canvas. */
  readonly el: HTMLDivElement;
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D | null;
  private readonly labelEl: HTMLSpanElement | null;
  private readonly valueEl: HTMLSpanElement | null;
  private readonly valueText: string | (() => string) | undefined;
  private readonly segments: number;
  /** BL-63 — precomputed per-segment ramps for the heat-fill path (fixed). */
  private readonly segmentRamps: Ramp[] | undefined;
  private readonly variant: ProBarVariant;
  private ramp: Ramp;
  private color: string;
  private value: number;
  private max: number;
  private state: ProBarState;
  private flashElapsed: number = FLASH_MS; // start settled
  private raf = 0;
  private lastTs = 0;
  private destroyed = false;

  constructor(opts: ProBarOptions) {
    ProBar.ensureStyles();
    this.color = opts.color;
    this.ramp = deriveRamp(opts.color);
    this.max = opts.max;
    this.value = opts.value ?? 0;
    this.state = opts.state ?? "active";
    this.segmentRamps =
      opts.segmentColors && opts.segmentColors.length > 1
        ? opts.segmentColors.map((c) => deriveRamp(c))
        : undefined;
    this.segments = this.segmentRamps
      ? this.segmentRamps.length
      : (opts.segments ?? 0);
    this.variant = opts.variant ?? "bar";
    this.valueText = opts.valueText;

    this.el = document.createElement("div");
    this.el.className = [
      "probar",
      `probar--${this.variant}`,
      ...(opts.extraClasses ?? []),
    ].join(" ");
    this.el.style.setProperty("--probar-mid", this.ramp.mid);
    this.el.style.setProperty("--probar-core", this.ramp.core);
    this.el.style.setProperty("--probar-rim", this.ramp.rim);

    this.canvas = document.createElement("canvas");
    this.canvas.className = "probar-mat";
    this.canvas.setAttribute("aria-hidden", "true");
    this.ctx = this.canvas.getContext("2d");
    this.el.appendChild(this.canvas);

    // Crisp DOM text layer (the readability half). Only built when needed.
    if (opts.label != null || opts.valueText !== "") {
      const body = document.createElement("div");
      body.className = "probar-body";
      if (opts.label != null) {
        this.labelEl = document.createElement("span");
        this.labelEl.className = "probar-label";
        this.labelEl.textContent = opts.label;
        body.appendChild(this.labelEl);
      } else {
        this.labelEl = null;
      }
      if (opts.valueText !== "") {
        this.valueEl = document.createElement("span");
        this.valueEl.className = "probar-value";
        body.appendChild(this.valueEl);
      } else {
        this.valueEl = null;
      }
      this.el.appendChild(body);
    } else {
      this.labelEl = null;
      this.valueEl = null;
    }

    this.syncValueText();
  }

  /** Mount, observe size, and paint. Call once the el is in the DOM. */
  mount(): void {
    if (typeof ResizeObserver !== "undefined") {
      const ro = new ResizeObserver(() => this.render());
      ro.observe(this.el);
      this.resizeObs = ro;
    }
    this.render();
  }

  private resizeObs: ResizeObserver | null = null;

  /** Update the value; a real change (re)triggers the §6.8 flash + a redraw. */
  setValue(value: number): void {
    if (value === this.value) return;
    this.value = value;
    this.flashElapsed = 0;
    this.syncValueText();
    this.render();
    this.startFlash();
  }

  /** Update the full-scale max (e.g. a heal that raises the cap). */
  setMax(max: number): void {
    if (max === this.max) return;
    this.max = max;
    this.syncValueText();
    this.render();
  }

  /** Re-tint the bar (rebuilds the shared ramp + the CSS vars). */
  setColor(color: string): void {
    if (color === this.color) return;
    this.color = color;
    this.ramp = deriveRamp(color);
    this.el.style.setProperty("--probar-mid", this.ramp.mid);
    this.el.style.setProperty("--probar-core", this.ramp.core);
    this.el.style.setProperty("--probar-rim", this.ramp.rim);
    this.render();
  }

  setState(state: ProBarState): void {
    if (state === this.state) return;
    this.state = state;
    this.el.dataset.state = state;
    this.render();
  }

  /** True while the value-flash is still animating (dirty-redraw gating). */
  isFlashing(): boolean {
    return this.flashElapsed < FLASH_MS && motionScale() > 0;
  }

  private syncValueText(): void {
    if (!this.valueEl) return;
    const txt =
      typeof this.valueText === "function"
        ? this.valueText()
        : (this.valueText ?? `${Math.round(this.value)}/${this.max}`);
    this.valueEl.textContent = txt;
  }

  /** Dirty-redraw the lit material at the current box (+ optional shared bloom).
   *  Tolerates a null ctx (headless → the CSS `--probar-mid` rung carries it). */
  render(): void {
    const ctx = this.ctx;
    if (!ctx || this.destroyed) return;
    const cw = this.el.clientWidth;
    const ch = this.el.clientHeight;
    if (cw <= 0 || ch <= 0) return;

    const dpr = Math.max(
      1,
      displayDpr(),
    );
    const W = cw + OVERSCAN * 2;
    const H = ch + OVERSCAN * 2;
    this.canvas.width = Math.round(W * dpr);
    this.canvas.height = Math.round(H * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, W, H);

    const glow = STATE_GLOW[this.state];
    const bx = OVERSCAN;
    const by = this.variant === "underline" ? OVERSCAN + ch - 3 : OVERSCAN;
    const bh = this.variant === "underline" ? 3 : ch;
    drawProBar(ctx, bx, by, cw, bh, {
      ramp: this.ramp,
      ratio: fillRatio(this.value, this.max),
      glow,
      segments: this.segments,
      flash: flashAlpha(this.flashElapsed),
      variant: this.variant,
      segmentRamps: this.segmentRamps,
    });

    // Route through the shared WebGL bloom (the same GLSL as the arena/HUD).
    // The bright leading edge clears UI_BLOOM.threshold and flares. Skipped for
    // dim states (no hot edge to bloom) and absent in headless.
    if (glow >= UI_BLOOM.threshold) {
      const bloom = getSharedUIBloom();
      if (bloom) {
        bloom.setDpr(dpr);
        const out = bloom.process(this.canvas);
        if (out) {
          ctx.setTransform(1, 0, 0, 1, 0, 0);
          ctx.globalAlpha = 0.9;
          ctx.drawImage(out, 0, 0, this.canvas.width, this.canvas.height);
          ctx.globalAlpha = 1;
        }
      }
    }
  }

  // The §6.8 value-flash self-drives a finite RAF that settles by FLASH_MS.
  private startFlash(): void {
    if (isReducedMotion()) {
      this.flashElapsed = FLASH_MS; // settle instantly; the value already updated
      this.render();
      return;
    }
    if (this.raf) return; // already running
    this.lastTs = now();
    const tick = (): void => {
      if (this.destroyed) return;
      const ts = now();
      this.flashElapsed = Math.min(FLASH_MS, this.flashElapsed + (ts - this.lastTs));
      this.lastTs = ts;
      this.render();
      if (this.flashElapsed < FLASH_MS) {
        this.raf = requestAnimationFrame(tick);
      } else {
        this.raf = 0;
      }
    };
    this.raf = requestAnimationFrame(tick);
  }

  destroy(): void {
    this.destroyed = true;
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
    this.resizeObs?.disconnect();
    this.resizeObs = null;
    this.el.remove();
  }

  // ─── One injected stylesheet for every bar (built FROM the §9 tokens) ────────

  private static stylesInjected = false;

  static ensureStyles(): void {
    if (ProBar.stylesInjected || typeof document === "undefined") return;
    ProBar.stylesInjected = true;
    const T = UI_TYPE;
    const style = document.createElement("style");
    style.id = "probar-styles";
    style.textContent = `
      .probar {
        position: relative; overflow: visible;
        display: flex; align-items: center;
        min-width: 0; box-sizing: border-box;
        font-family: ${UI_FONT_STACK};
        --probar-mid: ${COLORS.SENTINEL_GREEN};
      }
      /* The lit canvas — overscanned so the edge glow bleeds past the bar. */
      .probar-mat {
        position: absolute; inset: -${OVERSCAN}px;
        width: calc(100% + ${OVERSCAN * 2}px); height: calc(100% + ${OVERSCAN * 2}px);
        pointer-events: none; z-index: 0;
      }
      /* The crisp DOM text — never a bloom source. */
      .probar-body {
        position: relative; z-index: 1;
        display: flex; align-items: center; justify-content: space-between;
        width: 100%; gap: 8px; padding: 0 6px; box-sizing: border-box;
      }
      .probar-label {
        font-size: ${roleFontSize("micro")}; letter-spacing: ${T.caption.letterSpacing}px;
        text-transform: uppercase; color: var(--probar-core, ${T.caption.color});
        white-space: nowrap;
      }
      .probar-value {
        font-size: ${withUiScale(T.numeric.size - 2)}; letter-spacing: ${T.numeric.letterSpacing}px;
        font-variant-numeric: ${T.numeric.variantNumeric};
        color: ${T.numeric.color}; white-space: nowrap;
      }
      /* CSS lit-edge rung — the FINAL fallback when no canvas/WebGL paints the
         material (headless / BL-44). Real material wins when present. */
      .probar--bar {
        border: 1px solid color-mix(in srgb, var(--probar-mid) 55%, transparent);
        border-radius: 2px;
        background: linear-gradient(180deg, ${PANEL.BG} 0%, var(--probar-rim, #05070b) 50%, ${PANEL.BG} 100%);
      }
      .probar--underline { border-bottom: 2px solid var(--probar-mid); }
    `;
    document.head.appendChild(style);
  }
}

function now(): number {
  return typeof performance !== "undefined" ? performance.now() : Date.now();
}

/** Test/reset hook — drops the shared bloom + the injected style guard. */
export function __resetProBarForTest(): void {
  sharedBloom?.destroy();
  sharedBloom = undefined;
  ProBar["stylesInjected"] = false;
}
